
import React from 'react';
import { Link } from 'react-router-dom';
import SignupLink from '@/components/SignupLink';
import { Button } from '@/components/ui/button';
import { ChevronDown, Settings, CreditCard, LogOut, Loader2, Cookie } from 'lucide-react';
import { useLogout } from '@/hooks/useLogout';
import { openCookiePreferences } from '@/lib/cookieConsent';
import { QUICK_LINKS } from './NavLinks';

interface MobileMenuProps {
  isMenuOpen: boolean;
  isLandingPage: boolean;
  isLoggedIn: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenRegisterDialog: () => void;
  activeSection?: string;
  sections?: { href: string; label: string }[];
  chrome?: boolean;
  showQuickLinks?: boolean;
  showLandingActions?: boolean;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  isMenuOpen,
  isLandingPage,
  isLoggedIn,
  setIsMenuOpen,
  onOpenRegisterDialog,
  activeSection = 'home',
  sections,
  chrome = isLandingPage,
  showQuickLinks = isLandingPage && !sections,
  showLandingActions = isLandingPage,
}) => {
  const { handleLogout, isLoggingOut } = useLogout();
  if (!isMenuOpen) return null;

  const landingLinkClass =
    'block rounded-2xl px-4 py-3 text-left text-[0.9375rem] font-medium text-[#425064] transition-colors hover:bg-[#F1F8FE] hover:text-cs-primary';

  const landingSections: { href: string; label: string }[] = [
    { href: '#top', label: 'Home' },
    { href: '#about-us', label: 'About Us' },
    { href: '#features', label: 'Features' },
    { href: '#why-we-exist', label: 'Vision & Mission' },
    { href: '#pricing', label: 'Pricing' },
    { href: '#blogs', label: 'Blogs' },
  ];

  const items = sections ?? (isLandingPage ? landingSections : null);

  return (
    <div className={chrome ? 'lg:hidden mt-3 rounded-[1.5rem] border border-[#E7EEF2] bg-white p-3 shadow-[0_1rem_2.25rem_rgba(15,23,42,0.1)]' : 'md:hidden'}>
      <div className="space-y-2 pb-1 pt-1 text-left">
        {items && (
          <>
            {items.map(({ href, label }) => {
              const hashIndex = href.indexOf('#');
              const sectionId = (hashIndex >= 0 ? href.slice(hashIndex + 1) : href)
                .replace(/[^a-zA-Z0-9]+/g, '_')
                .replace(/^_|_$/g, '')
                .toLowerCase() || 'link';
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={href}
                  href={href}
                  className={landingLinkClass}
                  style={isActive ? { color: '#58D4F8' } : undefined}
                  onClick={() => setIsMenuOpen(false)}
                  data-analytics-event="Landing CTA Clicked"
                  data-analytics-id={`mobile_navlink_${sectionId}`}
                  data-analytics-label={label}
                  data-analytics-section="mobile_navbar_links"
                >
                  {label}
                </a>
              );
            })}
            {showQuickLinks ? (
              <details className="group rounded-2xl">
                <summary className={`${landingLinkClass} flex cursor-pointer list-none items-center justify-between`}>
                  Quick Links
                  <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" aria-hidden />
                </summary>
                <div className="mt-1 space-y-1 rounded-2xl bg-[#F8FBFD] p-2">
                  {QUICK_LINKS.map(({ href, label }) => (
                    <a
                      key={href}
                      href={href}
                      className="block rounded-xl px-3 py-2.5 text-[0.875rem] font-medium leading-5 text-[#425064] transition-colors hover:bg-white hover:text-cs-primary"
                      onClick={() => setIsMenuOpen(false)}
                      data-analytics-event="Landing CTA Clicked"
                      data-analytics-id={`mobile_navlink_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`}
                      data-analytics-label={label}
                      data-analytics-section="mobile_navbar_quick_links"
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </details>
            ) : null}
          </>
        )}
        <div className="space-y-2 border-t border-[#EDF3F7] pt-3">
          {showLandingActions ? (
            <>
              <Button
                asChild
                className="h-12 w-full rounded-[0.875rem] bg-cs-primary text-base font-semibold text-white hover:bg-cs-primary/90"
                onClick={() => setIsMenuOpen(false)}
              >
                <SignupLink
                  data-analytics-event="Landing CTA Clicked"
                  data-analytics-id="mobile_get_started"
                  data-analytics-label="Start for Free"
                  data-analytics-section="mobile_navbar"
                >
                  Start for Free
                </SignupLink>
              </Button>
              <Button
                asChild
                variant="ghost"
                className="h-12 w-full rounded-[0.875rem] border border-[#D9E3EA] bg-white text-base font-semibold text-[#1F2937] hover:bg-[#F8FBFD] hover:text-[#111827]"
                onClick={() => setIsMenuOpen(false)}
              >
                <Link
                  to="/login"
                  data-analytics-event="Landing CTA Clicked"
                  data-analytics-id="mobile_login_signup"
                  data-analytics-label="Login/Signup"
                  data-analytics-section="mobile_navbar"
                >
                  Login/Signup
                </Link>
              </Button>
            </>
          ) : isLoggedIn ? (
            <>
              <Button asChild onClick={() => setIsMenuOpen(false)} variant="outline" className="w-full border-cs-primary text-cs-primary hover:bg-cs-primary/10">
                <Link to="/profile?view=hub">
                  <Settings className="mr-2 h-4 w-4" />
                  My Settings
                </Link>
              </Button>
              <Button asChild onClick={() => setIsMenuOpen(false)} variant="outline" className="w-full border-cs-primary text-cs-primary hover:bg-cs-primary/10">
                <Link to="/profile?tab=billing">
                  <CreditCard className="mr-2 h-4 w-4" />
                  My Billing Profile
                </Link>
              </Button>
              <Button
                onClick={() => {
                  openCookiePreferences();
                  setIsMenuOpen(false);
                }}
                variant="outline"
                className="w-full border-cs-primary text-cs-primary hover:bg-cs-primary/10"
              >
                <Cookie className="mr-2 h-4 w-4" />
                Cookie Preferences
              </Button>
              <Button 
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }} 
                disabled={isLoggingOut}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoggingOut ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="mr-2 h-4 w-4 animate-pulse" />
                )}
                {isLoggingOut ? 'Logging out...' : 'Log Out'}
              </Button>
            </>
          ) : (
            <>
              <Button asChild onClick={() => setIsMenuOpen(false)} variant="outline" className="w-full border-cs-primary text-cs-primary hover:bg-cs-primary/10">
                <Link to="/login">Log In</Link>
              </Button>
              <Button asChild onClick={() => setIsMenuOpen(false)} className="w-full bg-cs-primary text-white hover:bg-cs-primary/90">
                <SignupLink>Sign Up</SignupLink>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;
